/**
 * 手游 Stats 月度/生命周期留存下载弹窗：数据源 ↔ 月度留存(mr)/生命周期留存(lr) 联动；
 * 勾选 Sensor Tower 时 mr/lr 互斥。
 */

export const isMobileRetentionStatsDownloadBusiness = (
  businessType: string,
): boolean =>
  businessType === 'mobile-stats-retentionmonthly' ||
  businessType === 'mobile-stats-retentionlifetime';

const MOBILE_STATS_METRIC_DOWNLOAD_BUSINESS = [
  'mobile-stats-countries',
  'mobile-stats-countries-rpd',
  'mobile-stats-retentionmonthly',
  'mobile-stats-retentionlifetime',
];

const MOBILE_STATS_RETENTION_METRICS = ['mr', 'lr'];
const MOBILE_STATS_RETENTION_DATE_TYPE = ['monthly'];

export const isMobileStatsMetricDownloadBusiness = (
  businessType: string,
): boolean => MOBILE_STATS_METRIC_DOWNLOAD_BUSINESS.includes(businessType);

export const hasMobileStatsRetentionMetric = (metricList?: string[]): boolean =>
  Array.isArray(metricList) &&
  metricList.some((metric) => MOBILE_STATS_RETENTION_METRICS.includes(metric));

export const getMobileStatsDownloadDateTypeList = (
  metricList: string[] = [],
  fallbackDateTypeList: string[] = [],
): string[] => {
  if (hasMobileStatsRetentionMetric(metricList)) {
    return MOBILE_STATS_RETENTION_DATE_TYPE;
  }
  return Array.isArray(fallbackDateTypeList) ? fallbackDateTypeList : [];
};

/** 与 Stats / Countries 页签 URL segment 一致（含「—」），顺序：生命周期 → ST 月度 → data.ai 月度 */
export function getMobileStatsRetentionDownloadTabDefaults(
  segment: string | undefined,
): { data_sources: string[]; metric: string[] } | null {
  if (!segment) {
    return null;
  }
  if (segment.includes('retention_lifetime')) {
    return { data_sources: ['sensortower'], metric: ['lr'] };
  }
  if (segment.includes('retention_monthly_sensortower')) {
    return { data_sources: ['sensortower'], metric: ['mr'] };
  }
  if (segment.includes('retention_monthly')) {
    return { data_sources: ['appannie'], metric: ['mr'] };
  }
  return null;
}

/**
 * 表单变更后修正互斥关系（自上而下 + 自下而上）
 */
export function sanitizeMobileRetentionStatsDownloadPatch(
  prevFormModel: Record<string, any>,
  patch: Record<string, any>,
): Record<string, any> {
  const merged = { ...prevFormModel, ...patch };
  let ds = [...(merged.data_sources ?? [])];
  let met = [...(merged.metric ?? [])];

  const hasDA = ds.includes('appannie');
  const hasST = ds.includes('sensortower');

  // 自下而上：勾选生命周期留存 → 去掉 data.ai
  if (met.includes('lr')) {
    ds = ds.filter((v: string) => v !== 'appannie');
  }

  // 自上而下：仅选择 data.ai → 去掉生命周期留存（选项也会隐藏）
  if (hasDA && !hasST) {
    met = met.filter((v: string) => v !== 'lr');
  }

  // 自上而下：同时选中 data.ai 与 ST → 生命周期不可选，移除已选
  if (hasDA && hasST) {
    met = met.filter((v: string) => v !== 'lr');
  }

  // 勾选 ST 时：月度留存(mr) 与 生命周期留存(lr) 仅能选一个
  if (hasST && met.includes('mr') && met.includes('lr')) {
    const prevMet = prevFormModel.metric ?? [];
    const prevMr = prevMet.includes('mr');
    const prevLr = prevMet.includes('lr');
    if (prevMr && !prevLr) {
      // 原先只有 mr，本次勾上了 lr → 保留 lr
      met = met.filter((v: string) => v !== 'mr');
    } else if (prevLr && !prevMr) {
      // 原先只有 lr，本次勾上了 mr → 保留 mr
      met = met.filter((v: string) => v !== 'lr');
    } else if (prevMr && prevLr) {
      met = met.filter((v: string) => v !== 'lr');
    } else {
      met = met.filter((v: string) => v !== 'lr');
    }
  }

  return {
    ...patch,
    data_sources: ds,
    metric: met,
    ...(hasMobileStatsRetentionMetric(met)
      ? { download_date_type: MOBILE_STATS_RETENTION_DATE_TYPE }
      : {}),
  };
}

/**
 * data_sources / metric 勾选框 disabled、生命周期选项是否展示
 */
export function patchMobileRetentionStatsDownloadFormList(
  formList: any[],
  formModel: Record<string, any>,
): any[] {
  const ds = formModel?.data_sources ?? [];
  const met = formModel?.metric ?? [];
  const hasDA = ds.includes('appannie');
  const hasST = ds.includes('sensortower');
  const hasLR = met.includes('lr');
  const hasRetentionMetric = hasMobileStatsRetentionMetric(met);

  const patchItem = (item: any): any => {
    const children = Array.isArray(item.children)
      ? item.children.map(patchItem)
      : item.children;

    if (item.field === 'data_sources' && item.type === 'checkboxGroup') {
      const options = (item.options ?? []).map((opt: any) => ({
        ...opt,
        disabled:
          opt.value === 'appannie' && hasLR ? true : !!opt.disabled,
      }));
      return { ...item, children, options };
    }
    if (item.field === 'metric' && item.type === 'checkboxGroup') {
      let options = item.options ?? [];
      if (hasDA && !hasST) {
        options = options.filter((opt: any) => opt.value !== 'lr');
      }
      options = options.map((opt: any) => {
        let disabled = !!opt.disabled;
        if (opt.value === 'lr' && hasDA && hasST) {
          disabled = true;
        }
        if (hasST && opt.value === 'lr' && met.includes('mr')) {
          disabled = true;
        }
        if (hasST && opt.value === 'mr' && met.includes('lr')) {
          disabled = true;
        }
        return { ...opt, disabled };
      });
      return { ...item, children, options };
    }
    if (item.field === 'download_date_type' && item.type === 'checkboxGroup') {
      const options = (item.options ?? []).map((opt: any) => ({
        ...opt,
        disabled: hasRetentionMetric && ['daily', 'weekly'].includes(opt.value)
          ? true
          : !!opt.disabled,
      }));
      return {
        ...item,
        children,
        options,
        defaultValue: hasRetentionMetric
          ? MOBILE_STATS_RETENTION_DATE_TYPE
          : item.defaultValue,
      };
    }
    return { ...item, children };
  };

  return formList.map(patchItem);
}
